// ============= Imports =============
import { useState, useCallback, useRef } from 'react';
import { fetchClients } from '../apis/clients';
import { fetchMatters } from '../apis/matters';
import { createDebounce } from '../utils/debounce';
import { UseAuth } from '../contexts/AuthContext';

// ============= Types =============
export interface Client {
  clientName: string;
  clientCode: string;
  clientUno: string;
}

export interface Matter {
  matterName: string;
  matterCode: string;
  matterUno: string;
}

interface FormattedClientData {
  label: string;
  value: string;
  clientName: string;
  clientCode: string;
}

interface FormattedMatterData {
  label: string;
  value: string;
  matterName: string;
  matterCode: string;
}

interface ApiResponse<T> {
  data: {
    items: T[];
  };
}

// ============= Hook =============
export const useClientMatterSearch = () => {
  // State management
  const { getAccessToken } = UseAuth();
  const [clientData, setClientData] = useState<string[]>([]);
  const [formattedClientData, setFormattedClientData] = useState<
    FormattedClientData[]
  >([]);
  const [formattedMatterData, setFormattedMatterData] = useState<
    FormattedMatterData[]
  >([]);
  const [matterData, setMatterData] = useState<string[]>([]);
  const [selectedClientUno, setSelectedClientUno] =
    useState<FormattedClientData>();
  const [selectedMatterUno, setSelectedMatterUno] =
    useState<FormattedMatterData>();

  // Add loading states
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isLoadingMatters, setIsLoadingMatters] = useState(false);

  // Use ref to track the latest search value
  const lastSearchRef = useRef<string>('');

  // Format helpers (keep as is)
  const formatClientData = useCallback(
    (client: Client): FormattedClientData => ({
      label: `${client.clientName} - (${client.clientCode})`,
      value: client.clientUno,
      clientName: client.clientName,
      clientCode: client.clientCode,
    }),
    []
  );

  const formatMatterData = useCallback(
    (matter: Matter): FormattedMatterData => ({
      label: `${matter.matterName} - (${matter.matterCode})`,
      value: matter.matterUno,
      matterName: matter.matterName,
      matterCode: matter.matterCode,
    }),
    []
  );

  // Debounced client search implementation
  const debouncedClientSearch = useCallback(
    createDebounce(async (searchValue: string) => {
      // Skip if the search value hasn't changed
      if (lastSearchRef.current === searchValue) return;
      lastSearchRef.current = searchValue;

      if (!searchValue.trim()) {
        setFormattedClientData([]);
        setClientData([]);
        setIsLoadingClients(false);
        return;
      }

      try {
        setIsLoadingClients(true);
        const response = await fetchClients({
          getAccessToken,
          searchValue: searchValue,
        });
        const data = response as unknown as ApiResponse<Client>;

        const formattedData = data.data.items.map(formatClientData);
        setFormattedClientData(formattedData);
        setClientData(formattedData.map((client) => client.label));
      } catch (error) {
        console.error('Error fetching clients:', error);
        setFormattedClientData([]);
        setClientData([]);
      } finally {
        setIsLoadingClients(false);
      }
    }, 300), // 300ms delay
    [formatClientData]
  );

  // Handle client search
  const handleClientSearch = useCallback(
    (searchValue: string) => {
      debouncedClientSearch(searchValue);
    },
    [debouncedClientSearch]
  );

  // Handle client selection
  const handleClientSelect = useCallback(
    async (selectedOption: string) => {
      const selectedClient = formattedClientData.find(
        (client) => client.label === selectedOption
      );

      if (!selectedClient) return;

      try {
        setIsLoadingMatters(true);
        setSelectedClientUno(selectedClient);
        const response = await fetchMatters({ getAccessToken,  searchValue: selectedClient.value });
        const data = response as unknown as ApiResponse<Matter>;

        const formattedData = data.data.items.map(formatMatterData);
        setFormattedMatterData(formattedData);
        setMatterData(formattedData.map((matter) => matter.label));
      } catch (error) {
        console.error('Error fetching matters:', error);
        setFormattedMatterData([]);
        setMatterData([]);
      } finally {
        setIsLoadingMatters(false);
      }
    },
    [formattedClientData, formatMatterData]
  );

  const handleMatterSelect = useCallback(
    (selectedOption: string) => {
      const selectedMatter = formattedMatterData.find(
        (matter) => matter.label === selectedOption
      );

      if (selectedMatter) {
        setSelectedMatterUno(selectedMatter);
      }
    },
    [formattedMatterData]
  );

  return {
    clientData,
    matterData,
    selectedClientUno,
    selectedMatterUno,
    handleClientSearch,
    handleClientSelect,
    handleMatterSelect,
    isLoadingClients,
    isLoadingMatters,
  };
};
